import { useState, useCallback } from 'react';
import uploadFlowService from '../service/uploadFlow.service';

/**
 * Hook para gerenciar opções de tipos de cliente e serviço
 */
const useFlowOptions = (token, intl, userTypesLocalized, provisionTypesLocalized) => {
  const [clientTypeOptions, setClientTypeOptions] = useState([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);

  const translateUserType = useCallback((optionName, id) => {
    const match = userTypesLocalized.find((ut) => ut.optionName === optionName);
    return match
      ? { value: optionName, label: match.label }
      : { value: optionName, label: optionName };
  }, [userTypesLocalized]);

  const translateServiceType = useCallback((optionName, id) => {
    const match = provisionTypesLocalized.find((pt) => pt.value === id);
    return match
      ? { value: optionName, label: match.label }
      : { value: optionName, label: optionName };
  }, [provisionTypesLocalized]);

  const processFlowOptionsForEdit = useCallback((flow, localizedUserTypes, localizedServices) => {
    // Traduzir e unir opções já cadastradas no flow (allowedClientTypes)
    const extraClientTypes = (Array.isArray(flow.allowedClientTypes) ? flow.allowedClientTypes : [])
      .map((clientType) => {
        const value = clientType?.value || clientType;
        let found = localizedUserTypes.find((opt) => opt.value === value);
        if (found) return found;
        const match = userTypesLocalized.find((ut) => ut.optionName === value);
        return match
          ? { value, label: match.label }
          : { value, label: value };
      });

    // Unir e remover duplicatas
    const finalUserTypes = [...localizedUserTypes, ...extraClientTypes].filter(
      (v, i, arr) => arr.findIndex((o) => o.value === v.value) === i
    ).sort((a, b) => a.label.localeCompare(b.label));

    // Traduzir e unir opções já cadastradas no flow (allowedServiceTypes)
    const extraServiceTypes = (Array.isArray(flow.allowedServiceTypes) ? flow.allowedServiceTypes : [])
      .map((serviceType) => {
        const value = serviceType?.value || serviceType;
        let found = localizedServices.find((opt) => opt.value === value);
        if (found) return found;
        const match = provisionTypesLocalized.find((pt) => pt.value === value || pt.value === Number(value));
        return match
          ? { value, label: match.label }
          : { value, label: value };
      });

    const finalServices = [...localizedServices, ...extraServiceTypes].filter(
      (v, i, arr) => arr.findIndex((o) => o.value === v.value) === i
    ).sort((a, b) => a.label.localeCompare(b.label));

    return { finalUserTypes, finalServices };
  }, [userTypesLocalized, provisionTypesLocalized]);

  const mapSelectedValues = useCallback((flow, localizedUserTypes, localizedServices) => {
    // Mapear os valores selecionados para o formato correto
    const mappedAllowedClientTypes = (Array.isArray(flow.allowedClientTypes) ? flow.allowedClientTypes : [])
      .map((clientType) => {
        const value = clientType?.value || clientType;
        return localizedUserTypes.find((opt) => opt.value === value) || { value, label: value };
      });

    const mappedAllowedServiceTypes = (Array.isArray(flow.allowedServiceTypes) ? flow.allowedServiceTypes : [])
      .map((serviceType) => {
        const value = serviceType?.value || serviceType;
        return localizedServices.find((opt) => opt.value === value) || { value, label: value };
      });

    return { mappedAllowedClientTypes, mappedAllowedServiceTypes };
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const profilesRes = await uploadFlowService.getProfiles(token);
      const { clientTypes = [], servicesList = [] } = profilesRes.data || {};

      const localizedUserTypes = clientTypes
        ? clientTypes.map((item) => translateUserType(item.optionName, item.id))
        : [];

      const localizedServices = servicesList
        ? servicesList.map((item) => translateServiceType(item.optionName, item.id))
        : [];

      setClientTypeOptions(localizedUserTypes.sort((a, b) => a.label.localeCompare(b.label)));
      setServiceTypeOptions(localizedServices.sort((a, b) => a.label.localeCompare(b.label)));

      return { localizedUserTypes, localizedServices };
    } catch (err) {
      setClientTypeOptions([]);
      setServiceTypeOptions([]);
      return { localizedUserTypes: [], localizedServices: [] };
    }
  }, [token, translateUserType, translateServiceType]);

  return {
    clientTypeOptions,
    serviceTypeOptions,
    setClientTypeOptions,
    setServiceTypeOptions,
    loadOptions,
    processFlowOptionsForEdit,
    mapSelectedValues
  };
};

export default useFlowOptions;